/**
 * NavigationSidebar.jsx — Phase N3.2 unified sidebar + hamburger.
 *
 * ONE component drives BOTH:
 *   - Desktop viewport (>= 1024px): persistent left rail, always visible
 *   - Mobile viewport (< 1024px):   full-screen modal overlay, opens
 *                                    via the hamburger button in AppNav
 *
 * Behavior (per user directive):
 *   - Mobile: one-open-group-at-a-time, page-behind scroll locked,
 *             auto-close on item selection, focus trap, Escape closes,
 *             ARIA role="dialog" aria-modal="true"
 *   - Desktop: multi-expand allowed, no focus trap, ARIA
 *              role="navigation"
 *   - Both:   current-page's group auto-expands, `aria-current="page"`
 *             on active item, empty groups dropped
 *
 * Visual treatment (Wave 4 v2 executive retune — supersedes the N3.2
 * saturated-purple treatment):
 *   - Sidebar bg:  Muted deep aubergine-neutral `--ds-color-nav`
 *                  (darker + less saturated than LandingV2's aubergine
 *                  top bar so the 260px sidebar column doesn't read as
 *                  brand-heavy purple at scale — Mercury/Ramp/Linear
 *                  pattern).
 *   - Active item: 2px vertical accent bar on the left edge in
 *                  `--ds-color-nav-active-bar` (soft executive purple),
 *                  NO background fill. Text bumps to `--ds-color-nav-
 *                  text-active` + semibold weight.
 *   - Hover:       Neutral white at 4% alpha — no purple tint.
 *   - Regular labels: `--ds-color-nav-text`  (rgba(255,255,255,0.88))
 *   - Group header:   `--ds-color-nav-text-subtle` (rgba(255,255,255,0.54))
 *
 * All chrome colors now flow through `--ds-color-nav-*` tokens — no
 * hex literals in this file. Palette can be retuned globally from
 * `tokens.css` without touching component code.
 *
 * The sidebar toggles a `nav-tree-active` class on `<body>` while
 * mounted so the app-wide CSS in `index.css` can reserve left-padding
 * on desktop. This means every page that mounts AppNav (and therefore
 * this sidebar) auto-shifts on desktop without touching page content.
 * Removes the class on unmount to keep unauthenticated pages clean.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { ChevronDown, X } from "lucide-react";
import { useAuth } from "@/auth/AuthContext";
import { useFeatureFlags } from "@/feature-flags/FeatureFlagsContext";
import {
  NAV_TREE,
  ADMIN_NAV,
  filterTree,
  findGroupForPath,
  findActiveItemKey,
} from "@/config/navigationTree";

const SIDEBAR_WIDTH_PX = 260;
const DESKTOP_MIN_WIDTH_PX = 1024;

// Executive sidebar palette — Wave 4 v2 retune. All values now flow
// through `--ds-color-nav-*` CSS variables defined in tokens.css so
// the sidebar chrome is design-system-native (no hex literals here).
// The `C` object is retained as a thin var-alias map so the render
// code below stays readable; every value is a CSS-var reference.
const C = Object.freeze({
  bg:            "var(--ds-color-nav)",
  bgDeep:        "var(--ds-color-nav-deep)",
  activeBar:     "var(--ds-color-nav-active-bar)",
  brandMark:     "var(--ds-color-nav-brand-mark)",
  text:          "var(--ds-color-nav-text)",
  textActive:    "var(--ds-color-nav-text-active)",
  textSubtle:    "var(--ds-color-nav-text-subtle)",
  border:        "var(--ds-color-nav-border)",
  borderStrong:  "var(--ds-color-nav-border-strong)",
  hover:         "var(--ds-color-nav-hover)",
});

/* -------------------------------------------------------------------- */
/* Viewport hook                                                         */
/* -------------------------------------------------------------------- */

export function useIsDesktop() {
  const [isDesktop, setIsDesktop] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`).matches;
  });
  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia(`(min-width: ${DESKTOP_MIN_WIDTH_PX}px)`);
    const listener = (e) => setIsDesktop(e.matches);
    if (mql.addEventListener) {
      mql.addEventListener("change", listener);
      return () => mql.removeEventListener("change", listener);
    }
    mql.addListener(listener);
    return () => mql.removeListener(listener);
  }, []);
  return isDesktop;
}

/* -------------------------------------------------------------------- */
/* Reserve horizontal room on desktop via a body class                   */
/* -------------------------------------------------------------------- */

function useReserveDesktopSidebarSpace() {
  useEffect(() => {
    if (typeof document === "undefined") return undefined;
    document.body.classList.add("nav-tree-active");
    return () => document.body.classList.remove("nav-tree-active");
  }, []);
}

/* -------------------------------------------------------------------- */
/* Lock page-behind scroll when the mobile sheet is open                 */
/* -------------------------------------------------------------------- */

function useLockBodyScroll(active) {
  useEffect(() => {
    if (!active || typeof document === "undefined") return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [active]);
}

/* -------------------------------------------------------------------- */
/* Public API — mounted from AppNav                                      */
/* -------------------------------------------------------------------- */

export default function NavigationSidebar({ mobileOpen, onMobileClose }) {
  const location = useLocation();
  const { user } = useAuth();
  const { flags } = useFeatureFlags();
  const isDesktop = useIsDesktop();
  useReserveDesktopSidebarSpace();

  // Filter both trees for the current auth/flag context. Admin group
  // renders BELOW customer groups; non-admins get zero admin items and
  // the whole Admin group drops out.
  const customerTree = useMemo(() => filterTree(NAV_TREE, { user, flags }), [user, flags]);
  const adminTree = useMemo(() => filterTree(ADMIN_NAV, { user, flags }), [user, flags]);
  const fullTree = useMemo(() => [...customerTree, ...adminTree], [customerTree, adminTree]);

  // Auto-expand the current-page's group. On desktop we keep prior
  // expansions (multi-expand); on mobile we enforce one-open-at-a-time.
  const activeGroupKey = useMemo(
    () => findGroupForPath(fullTree, location.pathname),
    [fullTree, location.pathname]
  );
  const activeItemKey = useMemo(
    () => findActiveItemKey(fullTree, location.pathname),
    [fullTree, location.pathname]
  );

  // Expansion state. On desktop we default to expanding ONLY the
  // active group; the user can then expand others (multi-expand). On
  // mobile the state always has exactly one entry — the active or
  // most-recently-tapped group.
  const [expanded, setExpanded] = useState(() => {
    const s = new Set();
    if (activeGroupKey) s.add(activeGroupKey);
    return s;
  });

  // Re-sync expansion when the route changes — always ensure the
  // active group is expanded regardless of viewport.
  useEffect(() => {
    if (!activeGroupKey) return;
    setExpanded((prev) => {
      if (isDesktop) {
        if (prev.has(activeGroupKey)) return prev;
        const next = new Set(prev);
        next.add(activeGroupKey);
        return next;
      }
      // Mobile: one-open-at-a-time. Replace whatever was open.
      return new Set([activeGroupKey]);
    });
  }, [activeGroupKey, isDesktop]);

  const toggleGroup = useCallback(
    (groupKey) => {
      setExpanded((prev) => {
        const has = prev.has(groupKey);
        if (isDesktop) {
          const next = new Set(prev);
          if (has) next.delete(groupKey);
          else next.add(groupKey);
          return next;
        }
        // Mobile: replace-not-merge → one-open-at-a-time.
        return has ? new Set() : new Set([groupKey]);
      });
    },
    [isDesktop]
  );

  // -- Mobile modal wiring ----------------------------------------------------

  useLockBodyScroll(!isDesktop && mobileOpen);

  // Focus trap + Escape handling — only when the mobile sheet is open.
  const dialogRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  useEffect(() => {
    if (isDesktop || !mobileOpen) return undefined;
    previouslyFocusedRef.current = document.activeElement;
    // Focus first focusable node inside the dialog.
    const t = setTimeout(() => {
      const first = dialogRef.current?.querySelector(
        "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
      );
      first?.focus();
    }, 0);

    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        onMobileClose?.();
        return;
      }
      if (e.key !== "Tab") return;
      const root = dialogRef.current;
      if (!root) return;
      const nodes = Array.from(
        root.querySelectorAll(
          "button, [href], input, select, textarea, [tabindex]:not([tabindex='-1'])"
        )
      ).filter((el) => !el.hasAttribute("disabled") && el.offsetParent !== null);
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      clearTimeout(t);
      // Restore focus to the previously-focused element (typically the
      // hamburger button that opened the sheet).
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [isDesktop, mobileOpen, onMobileClose]);

  // Auto-close mobile sheet on route change (after user taps an item).
  const openRef = useRef(mobileOpen);
  useEffect(() => {
    openRef.current = mobileOpen;
  }, [mobileOpen]);
  useEffect(() => {
    if (!isDesktop && openRef.current) {
      onMobileClose?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // -- Render -----------------------------------------------------------------

  const isMobileVisible = !isDesktop && mobileOpen;

  // Desktop rail is always rendered when this component is mounted.
  // Mobile modal is a portal-free overlay rendered only when open.
  return (
    <>
      {/* Desktop persistent left rail */}
      {isDesktop && (
        <aside
          data-testid="nav-sidebar-desktop"
          role="navigation"
          aria-label="Sidebar navigation"
          className="hidden lg:flex flex-col fixed top-0 left-0 h-screen z-40 overflow-y-auto"
          style={{
            width: `${SIDEBAR_WIDTH_PX}px`,
            background: C.bg,
            borderRight: `1px solid ${C.borderStrong}`,
          }}
        >
          <SidebarBrand />
          <NavGroups
            tree={fullTree}
            expanded={expanded}
            onToggleGroup={toggleGroup}
            activeItemKey={activeItemKey}
            testIdPrefix="nav-sidebar-desktop"
          />
        </aside>
      )}

      {/* Mobile modal overlay */}
      {isMobileVisible && (
        <div
          data-testid="nav-sidebar-mobile-overlay"
          className="fixed inset-0 z-50 lg:hidden"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={onMobileClose}
        >
          <div
            ref={dialogRef}
            data-testid="nav-sidebar-mobile-dialog"
            role="dialog"
            aria-modal="true"
            aria-label="Main navigation"
            className="h-full flex flex-col"
            style={{
              width: `min(88vw, ${SIDEBAR_WIDTH_PX + 20}px)`,
              background: C.bg,
              borderRight: `1px solid ${C.borderStrong}`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 py-4 flex-shrink-0" style={{ borderBottom: `1px solid ${C.border}` }}>
              <SidebarBrand inline />
              <button
                type="button"
                data-testid="nav-sidebar-mobile-close"
                onClick={onMobileClose}
                aria-label="Close navigation"
                className="inline-flex h-9 w-9 items-center justify-center rounded-md focus:outline-none focus-visible:ring-2"
                style={{ color: C.text, background: "transparent" }}
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <NavGroups
                tree={fullTree}
                expanded={expanded}
                onToggleGroup={toggleGroup}
                activeItemKey={activeItemKey}
                testIdPrefix="nav-sidebar-mobile"
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
}

/* -------------------------------------------------------------------- */
/* Subcomponents                                                         */
/* -------------------------------------------------------------------- */

function SidebarBrand({ inline = false }) {
  return (
    <div
      className={inline ? "" : "px-5 py-5"}
      style={inline ? {} : { borderBottom: `1px solid ${C.border}` }}
    >
      <Link
        to="/dashboard"
        data-testid="nav-sidebar-brand"
        className="inline-flex items-center gap-2 font-semibold whitespace-nowrap"
        style={{ color: "var(--ds-color-nav-text-active)" }}
      >
        <span
          aria-hidden="true"
          className="inline-flex h-8 w-8 items-center justify-center rounded-md"
          style={{ background: C.brandMark }}
        >
          <svg viewBox="0 0 24 24" className="h-4 w-4" fill="none" stroke="white" strokeWidth={2.5}>
            <path d="M12 2 L4 6 L4 12 C4 17 8 21 12 22 C16 21 20 17 20 12 L20 6 Z" />
          </svg>
        </span>
        <span className="text-base tracking-tight">Informed OS</span>
      </Link>
    </div>
  );
}

function NavGroups({ tree, expanded, onToggleGroup, activeItemKey, testIdPrefix }) {
  return (
    <nav
      data-testid={`${testIdPrefix}-groups`}
      className="flex-1 py-3"
    >
      {tree.map((group) => (
        <NavGroup
          key={group.key}
          group={group}
          expanded={expanded.has(group.key)}
          onToggle={() => onToggleGroup(group.key)}
          activeItemKey={activeItemKey}
          testIdPrefix={testIdPrefix}
        />
      ))}
    </nav>
  );
}

function NavGroup({ group, expanded, onToggle, activeItemKey, testIdPrefix }) {
  const Icon = group.icon;
  return (
    <div data-testid={`${testIdPrefix}-group-${group.key}`} data-expanded={expanded ? "true" : "false"}>
      <button
        type="button"
        data-testid={`${testIdPrefix}-group-toggle-${group.key}`}
        onClick={onToggle}
        aria-expanded={expanded ? "true" : "false"}
        aria-controls={`${testIdPrefix}-group-panel-${group.key}`}
        className="w-full flex items-center justify-between px-5 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-1"
        style={{ color: C.text }}
      >
        <span className="flex items-center gap-3 text-sm font-medium">
          {Icon ? (
            <Icon className="h-4 w-4 flex-shrink-0" aria-hidden="true" style={{ color: C.textSubtle }} />
          ) : null}
          <span>{group.label}</span>
        </span>
        <ChevronDown
          className="h-4 w-4 transition-transform flex-shrink-0"
          style={{
            color: C.textSubtle,
            transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
          }}
          aria-hidden="true"
        />
      </button>
      {expanded && (
        <ul
          id={`${testIdPrefix}-group-panel-${group.key}`}
          data-testid={`${testIdPrefix}-group-panel-${group.key}`}
          className="pb-2"
        >
          {group.items.map((item) => (
            <NavItem
              key={item.key}
              item={item}
              isActive={item.key === activeItemKey}
              testIdPrefix={testIdPrefix}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

function NavItem({ item, isActive, testIdPrefix }) {
  return (
    <li>
      <Link
        to={item.path}
        data-testid={`${testIdPrefix}-item-${item.key}`}
        data-active={isActive ? "true" : "false"}
        aria-current={isActive ? "page" : undefined}
        className="flex items-center gap-2 pl-12 pr-5 py-2 text-sm transition-colors focus:outline-none focus-visible:ring-1"
        style={{
          color: isActive ? C.textActive : C.text,
          background: "transparent",
          fontWeight: isActive ? 600 : 400,
          // 2px vertical accent bar on active items (Mercury/Linear pattern).
          // `inset` boxShadow avoids the layout shift a border-left would cause.
          boxShadow: isActive ? `inset 2px 0 0 ${C.activeBar}` : "none",
          transitionDuration: "var(--ds-duration-fast)",
        }}
        onMouseEnter={(e) => {
          if (!isActive) e.currentTarget.style.background = C.hover;
        }}
        onMouseLeave={(e) => {
          if (!isActive) e.currentTarget.style.background = "transparent";
        }}
      >
        <span className="truncate">{item.label}</span>
      </Link>
    </li>
  );
}
